import { StorageError, toStorageError } from './errors';

export const DB_NAME = 'sonicforge-studio';
export const DB_VERSION = 2;
export const STORE_PROJECTS = 'projects';
export const STORE_BLOBS = 'assetBlobs';
/** Phase 3C: motion recipes (independent of projects). */
export const STORE_RECIPES = 'motionRecipes';
export type StoreName = typeof STORE_PROJECTS | typeof STORE_BLOBS | typeof STORE_RECIPES;

/** Stored binary payload for a local asset. Kept as ArrayBuffer for portable structured cloning. */
export interface StoredBlob {
  id: string;
  projectId: string;
  mimeType: string;
  data: ArrayBuffer;
}

/**
 * Database upgrade path. Each `oldVersion` branch falls through so that a user on any
 * previous database version is upgraded step by step.
 */
function upgrade(db: IDBDatabase, oldVersion: number): void {
  if (oldVersion < 1) {
    db.createObjectStore(STORE_PROJECTS, { keyPath: 'id' });
    const blobs = db.createObjectStore(STORE_BLOBS, { keyPath: 'id' });
    blobs.createIndex('projectId', 'projectId', { unique: false });
  }
  // v2 (Phase 3C) only adds a store; projects and blobs are untouched.
  if (oldVersion < 2) db.createObjectStore(STORE_RECIPES, { keyPath: 'id' });
}

/**
 * Opens the database. `onClosed` runs when another tab upgrades the schema and this
 * connection closes, so callers can drop a cached connection and reopen on next use.
 */
export function openDatabase(name = DB_NAME, factory: IDBFactory | undefined = globalThis.indexedDB, onClosed?: () => void): Promise<IDBDatabase> {
  if (!factory) {
    return Promise.reject(
      new StorageError('unavailable', 'IndexedDB is not available in this browser (private mode may disable it).'),
    );
  }
  return new Promise((resolve, reject) => {
    let request: IDBOpenDBRequest;
    try {
      request = factory.open(name, DB_VERSION);
    } catch (error) {
      reject(new StorageError('unavailable', 'Could not open browser storage.', [], { cause: error }));
      return;
    }
    request.onupgradeneeded = (event) => upgrade(request.result, event.oldVersion);
    request.onsuccess = () => {
      const db = request.result;
      // Another tab upgraded the schema: close so it can proceed; next access reopens.
      db.onversionchange = () => {
        db.close();
        onClosed?.();
      };
      resolve(db);
    };
    request.onerror = () =>
      reject(
        request.error?.name === 'VersionError'
          ? new StorageError('unavailable', 'A newer version of SonicForge was opened in another tab. Reload this page to continue.', [], { cause: request.error })
          : new StorageError('unavailable', 'Could not open browser storage.', [], { cause: request.error }),
      );
    request.onblocked = () =>
      reject(new StorageError('unavailable', 'Storage upgrade is blocked by another open SonicForge tab. Close it and retry.'));
  });
}

export function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Runs `work` inside a single IndexedDB transaction and resolves only once the
 * transaction has *committed*. If `work` throws (synchronously or asynchronously) or
 * any request fails, the transaction is aborted and no write in it is persisted.
 *
 * `work` must only await IndexedDB requests made on `tx`; awaiting unrelated promises
 * lets the transaction auto-commit early.
 */
export function runTransaction<T>(
  db: IDBDatabase,
  stores: StoreName[],
  mode: IDBTransactionMode,
  work: (tx: IDBTransaction) => T | Promise<T>,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction(stores, mode);
    } catch (error) {
      reject(toStorageError(error, 'Could not start a storage transaction.'));
      return;
    }
    let result: T;
    let failure: unknown;
    tx.oncomplete = () => resolve(result);
    tx.onabort = () => reject(toStorageError(failure ?? tx.error, 'The storage transaction was rolled back.'));
    tx.onerror = (event) => {
      // Let onabort report; prevent the default which would also surface on window.
      failure ??= tx.error ?? (event.target as IDBRequest | null)?.error;
    };
    const abort = (error: unknown) => {
      failure ??= error;
      try {
        tx.abort();
      } catch {
        // Already finished/aborting.
      }
    };
    try {
      Promise.resolve(work(tx)).then((value) => {
        result = value;
      }, abort);
    } catch (error) {
      abort(error);
    }
  });
}
